import Settings from "./Settings";
import { AuthToken } from "./AuthToken";

import ProcessingResult from "../framework/ProcessingResult";

import CustomerSsoConfigurationDto from "../classes/dtos/accountmodule/CustomerSsoConfigurationDto";
import CustomerSsoDomainDto from "../classes/dtos/accountmodule/CustomerSsoDomainDto";

// The API answers errors with a ProblemDetails: its detail explains the rule that was broken (invalid domain, ...).
async function readError(response: Response): Promise<string> {
    try {
        const problem = await response.json();
        if (problem?.detail) {
            return problem.detail;
        }
    } catch {
        // Not a JSON body: fall back to the status.
    }

    return "Error = " + response.status.toString();
}

async function call(method: string, action: string, body?: object): Promise<Response> {
    let authSession = await AuthToken.get();

    return fetch(Settings.api_url + "/CustomerSso/" + action, {
        method: method,
        mode: "cors",
        cache: "no-cache",
        credentials: 'include',
        headers: {
            "Content-Type": "application/json",
            "Authorization": "Bearer " + authSession.Token
        },
        body: body === undefined ? undefined : JSON.stringify(body)
    });
}

const CustomerSsoApi = {

    /** Returns null as result when the customer has not configured SSO yet. */
    GetConfigurationAsync: async function (): Promise<ProcessingResult<CustomerSsoConfigurationDto | null>> {
        const response = await call("GET", "GetConfiguration");

        if (response.status === 200) {
            const data = await response.json();
            const c = data.configuration;

            if (c === null || c === undefined) {
                return ProcessingResult.Ok(null);
            }

            return ProcessingResult.Ok(new CustomerSsoConfigurationDto(
                c.isEnabled,
                c.autoProvisionUsers ?? false,
                c.autoProvisionRole ?? 1,
                c.loginSlug ?? "",
                c.idpEntityId,
                c.idpSignOnUrl,
                c.idpMetadataUrl ?? "",
                c.idpSigningCertificate,
                c.provisioningStatus,
                c.provisioningError ?? "",
                c.serviceProviderMetadataUrl,
                c.domains.map((d: { domain: string; isVerified: boolean; verificationRecordName: string; verificationRecordValue: string; }) =>
                    new CustomerSsoDomainDto(d.domain, d.isVerified, d.verificationRecordName, d.verificationRecordValue))));
        }

        return ProcessingResult.Error(await readError(response));
    },

    SaveConfigurationAsync: async function (isEnabled: boolean, autoProvisionUsers: boolean, autoProvisionRole: number, loginSlug: string, idpEntityId: string, idpSignOnUrl: string, idpMetadataUrl: string, idpSigningCertificate: string, domains: string[]): Promise<ProcessingResult<boolean>> {
        const response = await call("PUT", "SaveConfiguration", {
            isEnabled: isEnabled,
            autoProvisionUsers: autoProvisionUsers,
            autoProvisionRole: autoProvisionRole,
            loginSlug: loginSlug,
            idpEntityId: idpEntityId,
            idpSignOnUrl: idpSignOnUrl,
            idpMetadataUrl: idpMetadataUrl === "" ? null : idpMetadataUrl,
            idpSigningCertificate: idpSigningCertificate,
            domains: domains
        });

        if (response.status === 200) {
            return ProcessingResult.Ok(true);
        }

        return ProcessingResult.Error(await readError(response));
    },

    VerifyDomainAsync: async function (domain: string): Promise<ProcessingResult<boolean>> {
        const response = await call("POST", "VerifyDomain", { domain: domain });

        if (response.status === 200) {
            return ProcessingResult.Ok(true);
        }

        return ProcessingResult.Error(await readError(response));
    }
};

export default CustomerSsoApi;
